import React, { Fragment, useEffect, useState, memo, useCallback } from "react";
import { Row, Col, Form, Badge } from "react-bootstrap";
import Card from "../../../components/Card"; 
import Chart from "react-apexcharts";
import LandingService from "../../../services/pbumku/LandingServices";
import CountUp from "react-countup";


const formatDate = (date, isEndOfDay = false) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const time = isEndOfDay ? '23:59:59' : '00:00:00';
  return `${yyyy}-${mm}-${dd} ${time}`;
};
const JenisSertifikatChart = ({ title, total, data = [] }) => {
  const getColorByTitle = (title) => {
    const colors = {
      "CPIB": "#0071ce", "CBIB": "#004d40", "CPOIB": "#f7941e",
      "CDOIB": "#003166", "CPPIB": "#e74c3c", "CPIB Kapal": "#f1c40f"
    };
    return colors[title] || "#95a5a6";
  };

  const chartColor = getColorByTitle(title);

  const options = {
    chart: { type: "bar", toolbar: { show: false }, fontFamily: 'Plus Jakarta Sans, sans-serif' },
    plotOptions: { 
      bar: { 
        horizontal: true, 
        barHeight: "55%", 
        borderRadius: 4,
        dataLabels: { position: "center" } 
      } 
    },
    dataLabels: { 
      enabled: true, 
      style: { colors: ["#fff"], fontSize: "12px", fontWeight: 600 },
      formatter: (val) => val.toLocaleString('id-ID')
    },
    xaxis: { categories: data.map((d) => d.category), labels: { style: { fontSize: '10px' } } },
    yaxis: { labels: { maxWidth: 120, style: { fontSize: '11px', fontWeight: 500 } } },
    colors: [chartColor],
    grid: { show: false }
  };

  return (
    <Card className="border-0 shadow-sm h-100 overflow-hidden">
      <div style={{ height: '4px', backgroundColor: chartColor }}></div>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h6 className="fw-bold mb-0" style={{ color: chartColor }}>Top 5 Provinsi</h6>
            <small className="text-muted fw-bold">{title}</small>
          </div>
          <div className="text-end">
            <h4 className="fw-bold mb-0">
               <CountUp end={total} duration={2} />
            </h4>
            <small className="text-muted" style={{ fontSize: '10px' }}>TOTAL SERTIFIKAT</small>
          </div>
        </div>
        
        {data.length > 0 ? (
          <Chart 
            options={options} 
            series={[{ name: "Jumlah", data: data.map((d) => d.value) }]} 
            type="bar" 
            height={200} 
          />
        ) : (
          <div className="text-center py-5"><small className="text-muted">Data tidak tersedia</small></div>
        )}
      </Card.Body>
    </Card>
  );
};

const ReportUmku = memo(() => {
  const [jenisData, setJenisData] = useState([]);
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const jenisIzinList = [
    { title: "CPIB", kd_izin: "032000000034" },
    { title: "CBIB", kd_izin: "032000000068" },
    { title: "CPPIB", kd_izin: "032000000014" },
    { title: "CPOIB", kd_izin: "032000000019" },
    { title: "CDOIB", kd_izin: "032000000036" },
    { title: "CPIB Kapal", kd_izin: "032000000033" },
  ];

  const fetchAllJenisData = useCallback(async () => {
    setLoading(true);
    const startYear = new Date(selectedYear, 0, 1);
    const endYear = new Date(selectedYear, 11, 31);

    try {
      const promises = jenisIzinList.map(async (izin) => {
        try {
          const response = await LandingService.getPropinsiPerIzin(
            formatDate(startYear),
            formatDate(endYear, true),
            izin.kd_izin
          );
          const dataRaw = response.data || response || [];
          const data = Array.isArray(dataRaw)
            ? dataRaw
                .map((item) => ({
                  category: item.URAIAN_PROPINSI || "N/A",
                  value: parseInt(item.Jumlah) || 0,
                }))
                .sort((a, b) => b.value - a.value)
                .slice(0, 5)
            : [];
          const total = data.reduce((sum, item) => sum + item.value, 0);
          return { title: izin.title, total, data };
        } catch (err) {
          return { title: izin.title, total: 0, data: [] };
        }
      });
      const results = await Promise.all(promises);
      setJenisData(results);
    } catch (error) {
      console.error("Global fetch error", error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchAllJenisData();
  }, [fetchAllJenisData]);

  return (
    <Fragment>
      <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded shadow-sm">
        <div>
          <h3 className="mb-1 fw-bold text-danger">Dashboard Sertifikasi Mutu Primer</h3>
          <p className="text-muted small mb-0">Statistik Laporan per jenis izin dan provinsi tahun {selectedYear}</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Form.Label className="me-1 mb-0 small text-muted"></Form.Label>
          <Form.Select 
            size="sm" 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            disabled={loading}
            className="border-primary"
            style={{ width: 'fit-content', minWidth: '120px' }} 
          >
            {years.map(year => <option key={year} value={year}>{year}</option>)}
          </Form.Select>
          {loading && <Badge bg="warning" className="ms-2">Updating...</Badge>}
        </div>
      </div>

      <Row className="gy-4">
        {loading && jenisData.length === 0 ? (
          <Col className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <h5 className="mt-3 text-muted">Memuat Data Statistik...</h5>
          </Col>
        ) : (
          jenisData.map((item, index) => (
            <Col xs={12} md={6} lg={4} key={index}>
              <JenisSertifikatChart
                title={item.title}
                total={item.total}
                data={item.data}
              />
            </Col>
          ))
        )}
      </Row>
    </Fragment>
  );
});

export default ReportUmku;